/**
 * Self-schedule buffer defaults.
 *
 * BE wire shape (kula/core CORE-4350): flat per-audience fields
 * `interviewer_buffer_{enabled,pre_minutes,post_minutes}` (the FE wires
 * only the interviewer audience) on every `/ats_interviews`,
 * `/ats_schedules`, and `/ats_schedules/settings` payload. Hydration
 * for plan-fed flows lives in `initialValues/fromPlan.ts` (create) +
 * `initialValues/fromEditApi.ts` (edit); the account-level fallback
 * for `self-schedule-adhoc` lives in `api/useAccountBuffer.ts`.
 *
 * These constants seed the wizard's shared state when none of those
 * paths produced a value — and back the auto-seed in
 * `DetailsStep.handleBufferChange` when the user flips toggle-on with
 * stored 0/0 minutes.
 */

export const DEFAULT_BUFFER_BEFORE_MINUTES = 15;
export const DEFAULT_BUFFER_AFTER_MINUTES = 15;
export const BUFFER_STEP_MINUTES = 5;
export const BUFFER_MIN_MINUTES = 0;
export const BUFFER_MAX_MINUTES = 120;

/**
 * Read a buffer-minutes value off the wire. The account-settings JSON
 * path stores integers, but the manual `/ats_interviews` FormData path
 * stringifies them (`"13"`), so accept numeric strings too. Returns
 * `undefined` for anything non-finite — including placeholder garbage
 * like the literal `"pre_minutes"` a mis-seeded record can carry — so
 * callers fall back to the defaults instead of hydrating a bad value.
 */
export function coerceBufferMinutes(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}
